import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTION_TYPES = [
  { value: 'rating', label: 'Rating (1-5 stars)' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'text', label: 'Short text' },
];

const emptyQuestion = () => ({
  id: `tmp_${Math.random().toString(36).slice(2, 10)}`,
  label: '',
  type: 'rating',
  required: true,
  options: [],
});

const ServicesTab = ({ departments = [], publicBaseUrl }) => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [viewEvalsFor, setViewEvalsFor] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [evalsLoading, setEvalsLoading] = useState(false);

  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
  });

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/services`, { headers: authHeader() });
      setServices(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const startCreate = () => {
    setEditing({
      id: null,
      name: '',
      description: '',
      department_id: '',
      questions: [emptyQuestion()],
      is_active: true,
    });
    setShowEditor(true);
  };

  const startEdit = (svc) => {
    setEditing({
      id: svc.id,
      name: svc.name || '',
      description: svc.description || '',
      department_id: svc.department_id || '',
      questions: (svc.questions || []).map((q) => ({ ...q, options: q.options || [] })),
      is_active: svc.is_active !== false,
    });
    setShowEditor(true);
  };

  const updateField = (field, val) => setEditing((prev) => ({ ...prev, [field]: val }));

  const updateQuestion = (idx, updater) =>
    setEditing((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === idx ? updater(q) : q)),
    }));

  const addQuestion = () =>
    setEditing((prev) => ({ ...prev, questions: [...prev.questions, emptyQuestion()] }));

  const removeQuestion = (idx) =>
    setEditing((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== idx),
    }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editing.name.trim()) return setError('Service name is required');
    for (const q of editing.questions) {
      if (!q.label.trim()) return setError('All questions need a label');
      if (q.type === 'multiple_choice' && (!q.options || q.options.filter((o) => o.trim()).length < 2)) {
        return setError(`Multiple-choice question "${q.label}" needs at least 2 options`);
      }
    }

    setSavingId('save');
    setError('');
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description || null,
        department_id: editing.department_id || null,
        questions: editing.questions.map((q) => ({
          id: q.id?.startsWith('tmp_') ? undefined : q.id,
          label: q.label.trim(),
          type: q.type,
          required: !!q.required,
          options: q.type === 'multiple_choice' ? q.options.filter((o) => o.trim()) : [],
        })),
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

      if (editing.id) {
        await axios.put(`${API}/services/${editing.id}`, { ...payload, is_active: editing.is_active }, { headers: authHeader() });
      } else {
        await axios.post(`${API}/services`, payload, { headers: authHeader() });
      }
      setShowEditor(false);
      setEditing(null);
      await fetchServices();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save service');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (svc) => {
    if (!window.confirm(`Delete service "${svc.name}"? This cannot be undone.`)) return;
    setSavingId(svc.id);
    try {
      await axios.delete(`${API}/services/${svc.id}`, { headers: authHeader() });
      await fetchServices();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete service');
    } finally {
      setSavingId(null);
    }
  };

  const toggleActive = async (svc) => {
    setSavingId(svc.id);
    try {
      await axios.put(
        `${API}/services/${svc.id}`,
        { is_active: !svc.is_active },
        { headers: authHeader() }
      );
      await fetchServices();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update service');
    } finally {
      setSavingId(null);
    }
  };

  const copyLink = (svc) => {
    const url = `${publicBaseUrl}/service-feedback/${svc.id}`;
    navigator.clipboard.writeText(url);
    alert(`Link copied:\n${url}`);
  };

  const openEvaluations = async (svc) => {
    setViewEvalsFor(svc);
    setEvalsLoading(true);
    try {
      const res = await axios.get(`${API}/services/${svc.id}/evaluations`, { headers: authHeader() });
      setEvaluations(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load evaluations');
    } finally {
      setEvalsLoading(false);
    }
  };

  return (
    <div data-testid="services-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Service Evaluations</h2>
        <button
          data-testid="services-add-btn"
          onClick={startCreate}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
        >
          + Add Service
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading services…</div>
      ) : services.length === 0 ? (
        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-10 text-center">
          <p className="text-gray-600 mb-3">No services yet.</p>
          <p className="text-sm text-gray-500 mb-4">
            Create custom evaluation forms for specific services (e.g., consultation,
            check-in, treatment). Each service gets its own shareable link.
          </p>
          <button
            onClick={startCreate}
            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
          >
            Create your first service
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((svc) => {
            const dept = departments.find((d) => d.id === svc.department_id);
            return (
              <div
                key={svc.id}
                className="bg-white border rounded-lg p-4 shadow-sm flex flex-col"
                data-testid={`service-card-${svc.id}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-lg">{svc.name}</h3>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      svc.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {svc.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {dept && (
                  <p className="text-xs text-gray-500 mb-1">Department: {dept.name}</p>
                )}
                {svc.description && (
                  <p className="text-sm text-gray-600 mb-2">{svc.description}</p>
                )}
                <p className="text-xs text-gray-500 mb-3">
                  {(svc.questions || []).length} question
                  {(svc.questions || []).length !== 1 && 's'}
                </p>

                <div className="mt-auto flex flex-wrap gap-2">
                  <button
                    onClick={() => copyLink(svc)}
                    data-testid={`service-copy-${svc.id}`}
                    className="text-xs bg-indigo-500 text-white px-3 py-1.5 rounded hover:bg-indigo-600"
                  >
                    Copy Link
                  </button>
                  <button
                    onClick={() => openEvaluations(svc)}
                    data-testid={`service-evals-${svc.id}`}
                    className="text-xs bg-gray-700 text-white px-3 py-1.5 rounded hover:bg-gray-800"
                  >
                    View Evaluations
                  </button>
                  <button
                    onClick={() => startEdit(svc)}
                    data-testid={`service-edit-${svc.id}`}
                    className="text-xs bg-yellow-500 text-white px-3 py-1.5 rounded hover:bg-yellow-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => toggleActive(svc)}
                    disabled={savingId === svc.id}
                    className="text-xs bg-gray-200 text-gray-700 px-3 py-1.5 rounded hover:bg-gray-300 disabled:opacity-50"
                  >
                    {svc.is_active ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => handleDelete(svc)}
                    disabled={savingId === svc.id}
                    data-testid={`service-delete-${svc.id}`}
                    className="text-xs bg-red-500 text-white px-3 py-1.5 rounded hover:bg-red-600 disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Editor modal */}
      {showEditor && editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            data-testid="service-editor"
          >
            <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">
                {editing.id ? 'Edit Service' : 'Add Service'}
              </h3>
              <button
                onClick={() => {
                  setShowEditor(false);
                  setEditing(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSave} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service name *
                </label>
                <input
                  type="text"
                  data-testid="svc-editor-name"
                  value={editing.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className="w-full border rounded-md px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  data-testid="svc-editor-desc"
                  value={editing.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  className="w-full border rounded-md px-3 py-2"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link to department (optional)
                </label>
                <select
                  data-testid="svc-editor-dept"
                  value={editing.department_id}
                  onChange={(e) => updateField('department_id', e.target.value)}
                  className="w-full border rounded-md px-3 py-2"
                >
                  <option value="">— None —</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-900">Questions</h4>
                  <button
                    type="button"
                    onClick={addQuestion}
                    data-testid="svc-editor-add-q"
                    className="text-sm bg-blue-500 text-white px-3 py-1.5 rounded hover:bg-blue-600"
                  >
                    + Add question
                  </button>
                </div>

                {editing.questions.length === 0 && (
                  <p className="text-sm text-gray-500 italic">No questions yet.</p>
                )}

                <div className="space-y-3">
                  {editing.questions.map((q, idx) => (
                    <div key={q.id || idx} className="border rounded-md p-3 bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs text-gray-500">Question {idx + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeQuestion(idx)}
                          className="text-red-500 text-xs hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                      <input
                        type="text"
                        placeholder="Question label"
                        value={q.label}
                        onChange={(e) =>
                          updateQuestion(idx, (prev) => ({ ...prev, label: e.target.value }))
                        }
                        className="w-full border rounded-md px-3 py-2 mb-2"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={q.type}
                          onChange={(e) =>
                            updateQuestion(idx, (prev) => ({
                              ...prev,
                              type: e.target.value,
                              options: e.target.value === 'multiple_choice' ? prev.options : [],
                            }))
                          }
                          className="border rounded-md px-3 py-2"
                        >
                          {QUESTION_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                        <label className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={q.required}
                            onChange={(e) =>
                              updateQuestion(idx, (prev) => ({
                                ...prev,
                                required: e.target.checked,
                              }))
                            }
                          />
                          Required
                        </label>
                      </div>
                      {q.type === 'multiple_choice' && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 mb-1">Options (one per line)</p>
                          <textarea
                            value={(q.options || []).join('\n')}
                            onChange={(e) =>
                              updateQuestion(idx, (prev) => ({
                                ...prev,
                                options: e.target.value.split('\n'),
                              }))
                            }
                            rows={3}
                            className="w-full border rounded-md px-3 py-2 text-sm"
                            placeholder="Option A&#10;Option B&#10;Option C"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {editing.id && (
                <label className="flex items-center gap-2 text-sm text-gray-700 border-t pt-3">
                  <input
                    type="checkbox"
                    checked={editing.is_active}
                    onChange={(e) => updateField('is_active', e.target.checked)}
                  />
                  Service is active (visible at public link)
                </label>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="flex justify-end gap-2 border-t pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditor(false);
                    setEditing(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="svc-editor-save"
                  disabled={savingId === 'save'}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                >
                  {savingId === 'save' ? 'Saving…' : 'Save service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Evaluations viewer */}
      {viewEvalsFor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">
                Evaluations — {viewEvalsFor.name}
              </h3>
              <button
                onClick={() => {
                  setViewEvalsFor(null);
                  setEvaluations([]);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="px-6 py-4">
              {evalsLoading ? (
                <p className="text-gray-500">Loading…</p>
              ) : evaluations.length === 0 ? (
                <p className="text-gray-500 text-sm">No evaluations submitted yet.</p>
              ) : (
                <div className="space-y-3">
                  {evaluations.map((ev) => (
                    <div key={ev.id} className="border rounded-md p-3 bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-sm">
                            {ev.customer_name || 'Anonymous'}
                          </p>
                          {ev.mobile_number && (
                            <p className="text-xs text-gray-500">{ev.mobile_number}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="text-yellow-500 font-medium">
                            {'★'.repeat(ev.overall_rating || 0)}
                            {'☆'.repeat(5 - (ev.overall_rating || 0))}
                          </span>
                          <p className="text-xs text-gray-500">
                            {new Date(ev.submitted_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      {ev.additional_comments && (
                        <p className="text-sm text-gray-700 italic">
                          "{ev.additional_comments}"
                        </p>
                      )}
                      {ev.answers && Object.keys(ev.answers).length > 0 && (
                        <details className="mt-2 text-xs">
                          <summary className="cursor-pointer text-gray-600 hover:text-gray-900">
                            View answers
                          </summary>
                          <div className="mt-2 space-y-1">
                            {(viewEvalsFor.questions || []).map((q) => (
                              <div key={q.id} className="flex gap-2">
                                <span className="text-gray-500">{q.label}:</span>
                                <span className="text-gray-900 font-medium">
                                  {ev.answers[q.id] != null && ev.answers[q.id] !== ''
                                    ? String(ev.answers[q.id])
                                    : '—'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesTab;
